import apiService from '@/state/services/Api'
import _cloneDeep from 'lodash-es/cloneDeep.js'

const URL = 'meter-groups'

class DeviceGroup {
  constructor() {
    this.uuid = ''
    this.name = ''
    this.description = ''
    this.company = null
    this.installations = []
  }
}

const state = {
  deviceGroups: [],
  paginationData: {count: 0, previous: null, next: null},
  editedGroup: {uuid: null, mode: null, object: {}},
}

const getters = {
  // ===================================
  // Groups
  // ===================================

  deviceGroups(state) {
    return state.deviceGroups
  },

  paginationData(state) {
    return state.paginationData
  },

  getDeviceGroupByUuid: state => uuid => {
    return _cloneDeep(state.deviceGroups.find(e => e.uuid === uuid))
  },

  // ===================================
  // Edited group
  // ===================================

  editedGroup: state => {
    return _cloneDeep(state.editedGroup.object)
  },

  editedGroupUuid: state => {
    return state.editedGroup.uuid
  },

  editedGroupMode: state => {
    return state.editedGroup.mode
  },
}

const mutations = {
  // ===================================
  // Groups
  // ===================================

  SET_PAGINATION_DATA(state, data) {
    state.paginationData = data
  },

  SET_DEVICE_GROUPS(state, groups) {
    state.deviceGroups = groups
  },

  ADD_DEVICE_GROUP(state, group) {
    state.deviceGroups.push({...group})
  },

  REPLACE_DEVICE_GROUP(state, group) {
    state.deviceGroups = state.deviceGroups.map(e => (e.uuid === group.uuid ? group : e))
  },

  REMOVE_DEVICE_GROUP(state, uuid) {
    state.deviceGroups = state.deviceGroups.filter(e => e.uuid !== uuid)
  },

  // ===================================
  // Group devices
  // ===================================

  ADD_DEVICES_TO_GROUP(state, payload) {
    const group = state.deviceGroups.find(e => e.uuid === payload.uuid)
    if (!group) {
      return
    }
    group.installations.push(...payload.devices)
  },

  REMOVE_DEVICES_FROM_GROUP(state, payload) {
    const group = state.deviceGroups.find(e => e.uuid === payload.uuid)
    if (!group) {
      return
    }
    group.installations = group.installations.filter(e => !payload.devices.includes(e))
  },

  // ===================================
  // Edited group
  // ===================================

  SET_EDITED_DEVICE_GROUP: (state, groupData) => {
    state.editedGroup = groupData
  },

  SET_EDITED_DEVICE_GROUP_MODE: (state, mode) => {
    state.editedGroup.mode = mode
  },

  CLEAR_EDITED_DEVICE_GROUP: state => {
    state.editedGroup = {uuid: null, mode: null, object: {}}
  },
}

const actions = {
  async getDeviceGroups({commit, state}, refresh = true) {
    try {
      commit('SET_DEVICE_GROUPS', [])
      const url = !refresh && state.paginationData.next ? state.paginationData.next : URL
      const response = await apiService.get(url)
      commit(refresh ? 'SET_DEVICE_GROUPS' : 'ADD_DEVICE_GROUP', response.results)
      commit('SET_PAGINATION_DATA', {
        count: response.count,
        previous: response.previous,
        next: response.next,
      })
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async getDeviceGroupsBy({commit, state}, params) {
    try {
      return await apiService.get(URL, {params})
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async create({commit}, group) {
    try {
      const createdGroup = await apiService.post(URL, group)
      commit('ADD_DEVICE_GROUP', createdGroup)
      return Promise.resolve(createdGroup)
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async update({commit}, group) {
    try {
      const updatedGroup = await apiService.patch(`${URL}/${group.uuid}`, group)
      commit('REPLACE_DEVICE_GROUP', updatedGroup)
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async delete({commit}, uuid) {
    try {
      await apiService.delete(`${URL}/${uuid}`)
      commit('REMOVE_DEVICE_GROUP', uuid)
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async addDevices({commit}, payload) {
    try {
      const {group_uuid, device_uuids} = payload
      await apiService.post(`${URL}/${group_uuid}/add_installations`, {installations: device_uuids})
      commit('ADD_DEVICES_TO_GROUP', {uuid: group_uuid, devices: device_uuids})
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async removeDevices({commit}, payload) {
    try {
      const {group_uuid, device_uuids} = payload
      await apiService.post(`${URL}/${group_uuid}/delete_installations`, {
        installations: device_uuids,
      })
      commit('REMOVE_DEVICES_FROM_GROUP', {uuid: group_uuid, devices: device_uuids})
    } catch (e) {
      return Promise.reject(e)
    }
  },

  /**
   * Exports group installations
   * @param commit
   * @param {Object} payload Containing group uuids and format
   */
  async exportGroupsDevices({commit}, payload) {
    try {
      const {groupUuids, format} = payload
      if (!Array.isArray(groupUuids) || !groupUuids.length) {
        return
      }

      const config = {responseType: 'blob'}
      const urlSuffix = format ? `.${format}` : ''
      const toExport = groupUuids.join(';')
      const response = await apiService.get(`${URL}/export${urlSuffix}/?groups=${toExport}`, config)
      return Promise.resolve(response)
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async getAggregatedData({commit}, uuid) {
    try {
      const response = await apiService.get(`${URL}/${uuid}/reports`)
      return Promise.resolve(response)
    } catch (e) {
      return Promise.reject(e)
    }
  },

  // ===================================
  // Edited group
  // ===================================

  /**
   * Set group data to initialize edit
   *
   * @param commit
   * @param dispatch
   * @param getters
   * @param {Object} groupData
   */
  setEditedGroup: async ({commit, dispatch, getters}, groupData) => {
    switch (groupData.mode) {
      case 'creation':
        const editedGroup = {uuid: null, mode: 'creation', object: new DeviceGroup()}
        await commit('SET_EDITED_DEVICE_GROUP', editedGroup)
        return Promise.resolve()
      case 'edit':
        try {
          const g = getters.getDeviceGroupByUuid(groupData.uuid)
          const editedGroup = {uuid: groupData.uuid, mode: groupData.mode, object: g}
          await commit('SET_EDITED_DEVICE_GROUP', editedGroup)
          return Promise.resolve()
        } catch (e) {
          return Promise.reject()
        }
    }
  },

  updateEditedGroupMode: async ({commit}, mode) => {
    mode = !['creation', 'edit', 'readonly'].includes(mode) ? 'readonly' : mode
    commit('SET_EDITED_DEVICE_GROUP_MODE', mode)
  },

  /**
   * Clears group edit
   *
   * @param commit
   */
  clearEditedGroup: ({commit}) => {
    commit('CLEAR_EDITED_DEVICE_GROUP')
  },
}

export default {namespaced: true, state, getters, mutations, actions}
